<template>
  <div class="field-group" :class="state">
    <label v-if="label" class="field-label">
      {{ label }}
      <span v-if="required" class="required">*</span>
    </label>
    <div class="input-wrapper" :class="{ disabled }">
      <input
        ref="inputRef"
        :type="type === 'numeric' ? 'text' : type"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="modelValue"
        :maxlength="maxlength"
        :inputmode="type === 'numeric' ? 'numeric' : undefined"
        :pattern="type === 'numeric' ? '[0-9]*' : undefined"
        @beforeinput="onBeforeInput"
        @input="onInput"
        @keypress="onKeypress"
        @paste="onPaste"
      />
      <span v-if="$slots.icon" class="icon">
        <slot name="icon" />
      </span>
    </div>
    <div v-if="showMessage" class="field-message">
      <slot name="message-icon" />
      <span class="message-content">
        <span class="err-icon" v-if="state === 'error'">
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.33333 12.5C8.56944 12.5 8.76736 12.4201 8.92708 12.2604C9.08681 12.1007 9.16667 11.9028 9.16667 11.6667C9.16667 11.4306 9.08681 11.2326 8.92708 11.0729C8.76736 10.9132 8.56944 10.8333 8.33333 10.8333C8.09722 10.8333 7.89931 10.9132 7.73958 11.0729C7.57986 11.2326 7.5 11.4306 7.5 11.6667C7.5 11.9028 7.57986 12.1007 7.73958 12.2604C7.89931 12.4201 8.09722 12.5 8.33333 12.5ZM7.5 9.16667H9.16667V4.16667H7.5V9.16667ZM8.33333 16.6667C7.18056 16.6667 6.09722 16.4479 5.08333 16.0104C4.06944 15.5729 3.1875 14.9792 2.4375 14.2292C1.6875 13.4792 1.09375 12.5972 0.65625 11.5833C0.21875 10.5694 0 9.48611 0 8.33333C0 7.18056 0.21875 6.09722 0.65625 5.08333C1.09375 4.06944 1.6875 3.1875 2.4375 2.4375C3.1875 1.6875 4.06944 1.09375 5.08333 0.65625C6.09722 0.21875 7.18056 0 8.33333 0C9.48611 0 10.5694 0.21875 11.5833 0.65625C12.5972 1.09375 13.4792 1.6875 14.2292 2.4375C14.9792 3.1875 15.5729 4.06944 16.0104 5.08333C16.4479 6.09722 16.6667 7.18056 16.6667 8.33333C16.6667 9.48611 16.4479 10.5694 16.0104 11.5833C15.5729 12.5972 14.9792 13.4792 14.2292 14.2292C13.4792 14.9792 12.5972 15.5729 11.5833 16.0104C10.5694 16.4479 9.48611 16.6667 8.33333 16.6667Z" fill="#F85F35"/>
          </svg>
        </span>
        {{ message }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FieldInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    state: {
      type: String,
      default: 'default'
    },
    message: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    maxlength: {
      type: Number,
      default: null
    }
  },
  computed: {
    showMessage() {
      if (!this.message || !this.state) return false
      if (this.state) return true
    }
  },
  methods: {
    onBeforeInput(e) {
      if (this.type !== 'numeric') return

      // Cek tipe input
      const inputType = e.inputType
      const data = e.data

      // Jika insert text, validasi apakah angka
      if (inputType === 'insertText' && data) {
        if (!/^[0-9]$/.test(data)) {
          e.preventDefault()
        }
      }

      // Jika insert dari paste/drop, validasi
      if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop') {
        if (data && !/^\d*$/.test(data)) {
          e.preventDefault()
        }
      }
    },

    onKeypress(e) {
      if (this.type !== 'numeric') return

      // Cek apakah karakter yang diketik adalah angka
      const char = String.fromCharCode(e.keyCode || e.which)
      if (!/^[0-9]$/.test(char)) {
        e.preventDefault()
      }
    },

    onPaste(e) {
      if (this.type !== 'numeric') return

      e.preventDefault()

      // Ambil data yang di-paste
      const pastedData = e.clipboardData.getData('text')

      // Ambil hanya angka
      const numbersOnly = pastedData.replace(/\D/g, '')

      if (numbersOnly) {
        // Gabungkan dengan value yang sudah ada
        const input = this.$refs.inputRef
        const start = input.selectionStart
        const end = input.selectionEnd
        const currentValue = this.modelValue || ''

        // Insert di posisi cursor
        let newValue = currentValue.substring(0, start) + numbersOnly + currentValue.substring(end)

        // Apply maxlength
        if (this.maxlength) {
          newValue = newValue.slice(0, this.maxlength)
        }

        this.$emit('update:modelValue', newValue)

        // Set cursor position
        this.$nextTick(() => {
          const newPosition = start + numbersOnly.length
          input.setSelectionRange(newPosition, newPosition)
        })
      }
    },

    onInput(e) {
      let val = e.target.value

      if (this.type === 'numeric') {
        // Bersihkan semua non-digit
        const cleaned = val.replace(/\D/g, '')

        // Jika ada perubahan, update ke cleaned value
        if (cleaned !== val) {
          e.target.value = cleaned
          val = cleaned
        }
      }

      if (this.maxlength) {
        val = val.slice(0, this.maxlength)
      }

      this.$emit('update:modelValue', val)
    }
  }
}
</script>
<style scoped>
.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

/* LABEL */
.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #3c3f44;
}

.required {
  color: #f44336;
  margin-left: 2px;
}

/* INPUT WRAPPER */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper input {
  width: 100%;
  padding: 16px 40px 16px 14px;
  border-radius: 8px;
  border: 1.5px solid #d0d5dd;
  font-size: 14px;
  outline: none;
  background: #fff;
  transition: border-color 0.2s;
}

/* ICON */
.icon {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
}

/* MESSAGE */
.field-message {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
}

/* STATES */
.field-group.default input {
  border-color: #d0d5dd;
}

.field-group.default input:focus {
  border-color: #7b61ff;
}

/* ERROR */
.field-group.error input {
  border-color: #f44336;
}
.field-group.error .field-message {
  color: #f44336;
}

/* WARNING */
.field-group.warning input {
  border-color: #f5a544;
}
.field-group.warning .field-message {
  color: #f5a544;
}

/* SUCCESS */
.field-group.success input {
  border-color: #2e7d32;
}
.field-group.success .field-message {
  color: #2e7d32;
}

/* DISABLED */
.input-wrapper.disabled input {
  background: #f2f4f7;
  border-color: #e4e7ec;
  color: #98a2b3;
}
.input-wrapper.disabled input::placeholder {
  color: #98a2b3;
}

.field-message.default {
  color: #667085;
}

.field-message.error {
  color: #f44336;
}

.field-message.warning {
  color: #f5a544;
}

.field-message.success {
  color: #2e7d32;
}

/* Container message */
.field-message {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
}

/* Wrapper icon + text */
.message-content {
  display: flex;
  align-items: start;
  gap: 6px;
}

/* Error icon */
.err-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 4px;
}
</style>
